<template>
	<div class="content-module-container">
		<div class="content-module-block">
			<CommonTab type="normal" :value.sync="selectedModuleType" :option="moduleGroups" @change="changeModule" />
			<div class="query" v-if="!isResearchTab">
				<CommonSearch :value.sync="searchValue" class="mt-5 mr-48" @change="changeSearch" />
				<StockTab @change="handleStockChange" />
			</div>
		</div>

		<!-- 公告/调研记录列表 -->
		<MStateBox :loading="isLoading" :empty="isEmpty">
			<div class="announce-list">
				<FileInfoCard
					v-for="(item, index) in currentData"
					:key="index"
					:item="item"
					:formatter="isResearchTab ? (data => ({ fileUrl: data.url })) : (data => ({ fileUrl: data.comeinLink }))"
					class="announce-list--item"
					@click="handlePreview(item)"
				/>
			</div>
			<el-pagination
				v-if="pageTotal"
				@size-change="changePageSize"
				@current-change="changeCurrentPage"
				:current-page="currentPage"
				:page-sizes="pageSizes"
				:page-size="pageSize"
				:layout="pageLayout"
				:total="pageTotal"
				background
			></el-pagination>
		</MStateBox>
	</div>
</template>

<script setup>
import FileInfoCard from '@rebuild/materials/shared/common/FileInfoCard.vue';
import { ref, onMounted, computed, getCurrentInstance } from 'vue';
import { useCurrentContext } from '@rebuild/materials/shared/announcement/context';
import MStateBox from '@rebuild/components/MStateBox.vue';
import StockTab from '@/components/StockTab.vue';
import { useModuleData } from '@rebuild/hooks/data/useModuleData';
import { useModuleNavigation } from '@rebuild/hooks/useModuleNavigation';
import { usePaginationPC } from '@rebuild/hooks/load-more/usePaginationPC';
import dayjs from 'dayjs';

const RESEARCH_TAB_VALUE = '__research__';

const { proxy } = getCurrentInstance();
const { announcement, research } = useModuleData();
const { viewModule } = useModuleNavigation();

const selectedModuleType = ref(0);

const context = useCurrentContext();
const languageType = computed(() => context.languageType);
const tabContent = computed(() => context.tabContent);

const tabModules = ref([
	{ label: proxy.$t('enterprise.announcement.all'), value: '' },
	{ label: proxy.$t('enterprise.regular-announcement'), value: 'regular' },
	{ label: proxy.$t('enterprise.interim-announcement'), value: 'temporarily' }
]);

const researchTab = { label: '调研记录', value: RESEARCH_TAB_VALUE };

const moduleGroups = computed(() => {
	const base = tabContent.value?.length ? tabContent.value : tabModules.value;
	return [...base, researchTab];
});

const isResearchTab = computed(() => selectedModuleType.value === RESEARCH_TAB_VALUE);

const searchValue = ref('');

const currentData = computed(() => {
	if (isResearchTab.value) {
		const data = research.data?.rows || [];
		return data.map(item => ({
			...item,
			time: item.ptime
		}));
	}
	const data = announcement.data?.rows || [];
	return data.map(item => ({
		...item,
		typeText: item.parentClassification,
		time: dayjs(item.publishDate).format('YYYY-MM-DD')
	}));
});

const isLoading = computed(() => isResearchTab.value ? research.loading : announcement.loading);
const isEmpty = computed(() => (isResearchTab.value ? research.empty : announcement.empty) || !currentData.value.length);

const handlePaginationChange = () => {
	loadData();
};

const {
	currentPage,
	pageSize,
	pageSizes,
	pageLayout,
	pageTotal,
	changePageSize,
	changeCurrentPage,
	changePageTotal,
	refreshPagination
} = usePaginationPC({
	bindChange: handlePaginationChange
});

const loadData = async () => {
	if (isResearchTab.value) {
		await research.refresh({
			pagestart: currentPage.value - 1,
			pagenum: pageSize.value
		});
		changePageTotal(Number(research.data?.total || 0));
	} else {
		await announcement.refresh({
			useStockCode: true,
			classificationIds: selectedModuleType.value || '',
			pageStart: currentPage.value - 1,
			pageNum: pageSize.value,
			order: 'desc',
			title: searchValue.value,
			...languageType.value
		});
		changePageTotal(Number(announcement.data?.total || 0));
	}
};

const changeModule = ({ value }) => {
	selectedModuleType.value = value;
	refreshPagination();
};

const changeSearch = () => {
	refreshPagination();
};

const handleStockChange = () => {
	loadData();
};

const handlePreview = value => {
	viewModule(isResearchTab.value ? 'research' : 'announcement', value);
};

onMounted(() => {
	loadData();
});
</script>

<style lang="less" scoped>
.content-module-container {
	.content-module-block {
		display: flex;
		justify-content: space-between;
		.query {
			display: flex;
			flex-flow: row nowrap;
			align-items: center;
		}
	}
	.announce-list {
		margin-top: 35px;
		display: flex;
		flex-flow: row wrap;
		&--item {
			width: calc(50% - 20px);
			margin-bottom: 24px;
			&:nth-child(2n-1) {
				margin-right: 40px;
			}
		}
	}
}
</style>
